import type { Options as Html2CanvasOptions } from "html2canvas/dist/types";

// Render the displayed report itself, preserving browser-shaped Persian text.
export async function downloadVehicleReport(report: HTMLElement, code?: string) {
  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
    import("html2canvas"),
    import("jspdf"),
  ]);
  const renderCanvas = html2canvas as unknown as (
    element: HTMLElement,
    options: Partial<Html2CanvasOptions>,
  ) => Promise<HTMLCanvasElement>;
  await document.fonts.ready;

  const pdf = new jsPDF({ unit: "mm", format: "a4" });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 10;
  const contentWidth = pageWidth - margin * 2;
  let y = margin;
  const colorCache = new Map<string, string>();

  const sections = Array.from(report.children)
    .filter(
      (child): child is HTMLElement =>
        child instanceof HTMLElement &&
        (child.tagName === "HEADER" || child.tagName === "SECTION"),
    )
    .sort(
      (first, second) =>
        Number(first.dataset.reportOrder ?? Number.MAX_SAFE_INTEGER) -
        Number(second.dataset.reportOrder ?? Number.MAX_SAFE_INTEGER),
    );

  for (const section of sections) {
    let blocks: { top: number; bottom: number }[] = [];
    const canvas = await renderCanvas(section, {
      backgroundColor: "#ffffff",
      scale: 1.5,
      useCORS: true,
      windowWidth: 1100,
      ignoreElements: (element) => {
        if (element.hasAttribute("data-pdf-exclude")) return true;
        if (element.closest("head")) return false;
        return !element.contains(section) && !section.contains(element);
      },
      onclone: (clonedDocument, clonedSection) => {
        clonedSection.style.width = "850px";
        clonedSection.style.maxWidth = "none";
        const pdfStyle = clonedDocument.createElement("style");
        pdfStyle.textContent =
          "[data-vehicle-violations] tbody > tr { display: table-row !important; } [data-vehicle-report] [data-pdf-badge-text], [data-vehicle-report] span[style*='translateY'] { transform: translateY(-7px) !important; }";
        clonedDocument.head.appendChild(pdfStyle);
        const context = clonedDocument.createElement("canvas").getContext("2d");
        const normalizeColor = (color: string) => {
          if (!/oklch\(|oklab\(|lch\(|lab\(|color\(/i.test(color) || !context) return color;
          if (colorCache.has(color)) return colorCache.get(color)!;
          context.clearRect(0, 0, 1, 1);
          context.fillStyle = color;
          context.fillRect(0, 0, 1, 1);
          const [r, g, b, a] = context.getImageData(0, 0, 1, 1).data;
          const normalized = `rgba(${r}, ${g}, ${b}, ${a / 255})`;
          colorCache.set(color, normalized);
          return normalized;
        };
        const elements = [clonedDocument.documentElement, clonedDocument.body, clonedSection, ...Array.from(clonedSection.querySelectorAll<HTMLElement>("*"))];
        const properties = ["color", "background-color", "border-top-color", "border-right-color", "border-bottom-color", "border-left-color", "text-decoration-color", "-webkit-text-stroke-color"];
        const unsupportedColor = /oklch\(|oklab\(|lch\(|lab\(|color\(/i;
        const styles = elements.map((element) => {
          const style = clonedDocument.defaultView!.getComputedStyle(element);
          return {
            element,
            colors: properties.map((property) => [property, style.getPropertyValue(property)] as const),
            boxShadow: style.boxShadow,
            textShadow: style.textShadow,
          };
        });
        for (const { element, colors, boxShadow, textShadow } of styles) {
          // html2canvas draws spaced letters individually, breaking Persian shaping.
          element.style.setProperty("letter-spacing", "normal", "important");
          for (const [property, original] of colors) {
            const normalized = normalizeColor(original);
            if (normalized !== original) element.style.setProperty(property, normalized, "important");
          }
          if (unsupportedColor.test(boxShadow)) element.style.boxShadow = "none";
          if (unsupportedColor.test(textShadow)) element.style.textShadow = "none";
        }
        const top = clonedSection.getBoundingClientRect().top;
        blocks = Array.from(clonedSection.querySelectorAll("tr, [data-pdf-block], h1, h2, h3, p"))
          .map((element) => {
            const rect = element.getBoundingClientRect();
            return { top: rect.top - top, bottom: rect.bottom - top };
          });
      },
    });

    const pixelsPerMm = canvas.width / contentWidth;
    const fullPageHeight = Math.floor((pageHeight - margin * 2) * pixelsPerMm);
    const remainingHeight = Math.floor((pageHeight - margin - y) * pixelsPerMm);
    if (y > margin && canvas.height <= fullPageHeight && canvas.height > remainingHeight) {
      pdf.addPage();
      y = margin;
    }
    if (y > margin && canvas.height > fullPageHeight && remainingHeight < fullPageHeight * 0.35) {
      pdf.addPage();
      y = margin;
    }
    let offset = 0;
    while (offset < canvas.height) {
      let available = Math.floor((pageHeight - margin - y) * pixelsPerMm);
      if (available < 20 * pixelsPerMm) {
        pdf.addPage();
        y = margin;
        available = Math.floor((pageHeight - margin - y) * pixelsPerMm);
      }
      let height = Math.min(available, canvas.height - offset);
      if (offset + height < canvas.height) {
        const boundary = (offset + height) / 1.5;
        const overlapping = blocks.filter((block) => block.top < boundary && block.bottom > boundary);
        if (overlapping.length) {
          const safeHeight = Math.floor(Math.min(...overlapping.map((block) => block.top)) * 1.5 - offset);
          if (safeHeight > 10 * pixelsPerMm) {
            height = safeHeight;
          } else if (safeHeight >= 0 && y > margin) {
            pdf.addPage();
            y = margin;
            continue;
          }
        }
      }
      const slice = document.createElement("canvas");
      slice.width = canvas.width;
      slice.height = height;
      const context = slice.getContext("2d");
      if (!context) throw new Error("امکان ساخت تصویر گزارش وجود ندارد.");
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, slice.width, slice.height);
      context.drawImage(canvas, 0, offset, canvas.width, height, 0, 0, canvas.width, height);
      pdf.addImage(slice.toDataURL("image/jpeg", 0.95), "JPEG", margin, y, contentWidth, height / pixelsPerMm);
      offset += height;
      y += height / pixelsPerMm;
      if (offset < canvas.height) {
        pdf.addPage();
        y = margin;
      }
    }
    y += 4;
  }
  pdf.save(`vehicle-report${code ? `-${code.replace(/[^a-zA-Z0-9_-]/g, "")}` : ""}.pdf`);
}
